import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import styles from '../ChatInterface.module.css';
import EnhancedMarkdown from './enhanced/EnhancedMarkdown';
import { useRolesConfig } from '../hooks/useRolesConfig'; // 🆕 动态角色配置

// 🔧 常量：避免重复创建对象/函数
const EMPTY_OBJECT = {};
const MESSAGE_STYLE_BASE = { marginBottom: '2px', minHeight: '40px' };
const MESSAGE_STYLE_WITH_TOP = { marginBottom: '2px', minHeight: '40px', marginTop: '14px' };
const FULL_SIZE_STYLE = { height: '100%', width: '100%' };
const POINTER_STYLE = { cursor: 'pointer' };
const FOOTER_STYLE = { height: '20px' };

const getMessageGroupType = (allMessages, index) => {
  const currentMsg = allMessages[index];
  const prevMsg = index > 0 ? allMessages[index - 1] : null;

  if (!prevMsg) return 'first';
  if (currentMsg.role !== prevMsg.role) return 'newGroup';
  if (currentMsg.type !== prevMsg.type) return 'newGroup';

  return 'sameGroup';
};

const getAvatarName = (msg) => {
  if ((msg.type === "MsgtoHuman" || msg.type === "Draft") && (msg.role === "Expert" || msg.role === "Draft")) {
    return "tips";
  }
  if (msg.type === "MsgtoHuman" || msg.type === "System") return "tips";
  if (msg.type === "human") return "Human";
  if (msg.type === "expert_recommendations") return "tips";
  if (msg.role === "Expert") return "tips";
  return msg.role;
};

const getDisplayRoleName = (msg) => {
  if (msg.isWelcomeMessage && msg.agentName) return msg.agentName;
  if ((msg.type === "MsgtoHuman" || msg.type === "Draft") && msg.role === "Draft" && msg.agentName) {
    return `${msg.agentName} Draft`;
  }
  if ((msg.type === "MsgtoHuman" || msg.type === "StreamingMsgtoHuman") && msg.role === "Expert" && msg.agentName) return msg.agentName;
  if (msg.type === "MsgtoHuman" || msg.type === "System") return "Tips";
  if (msg.type === "human") return "Human";
  if (msg.type === "expert_recommendations") return "智能体推荐";
  return msg.role || "Unknown";
};

const MessageHeader = React.memo(({ msg, currentStyles, clickableAgentName, onAgentClick }) => {
  const tooltipAgentName = clickableAgentName || "";
  const isHeaderClickable = Boolean(clickableAgentName && onAgentClick);
  const avatarName = getAvatarName(msg);
  const roleName = getDisplayRoleName(msg);

  return (
    <div
      className={currentStyles.messageHeader}
      onClick={isHeaderClickable ? () => onAgentClick(clickableAgentName) : undefined}
      style={isHeaderClickable ? POINTER_STYLE : undefined}
      title={isHeaderClickable ? `单击呼叫 ${tooltipAgentName} 强制编写阶段文档` : undefined}
    >
      <img
        className={currentStyles.avatar}
        src={`/avatars/${avatarName}.png`}
        alt={msg.role || "Unknown"}
        loading="lazy"
      />
      <span className={currentStyles.roleName}>{roleName}</span>
      {msg.timestamp && (
        <span className={currentStyles.messageTime}>
          {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
        </span>
      )}
    </div>
  );
});

const MessageBody = React.memo(({ msg, index, currentStyles, renderMessageContent }) => {
  if (msg.isWelcomeMessage) {
    return (
      <div className={currentStyles.messageContentWrapper}>
        <EnhancedMarkdown
          content={msg.content}
          className={currentStyles.welcomeMessageContent}
        />
      </div>
    );
  }
  return renderMessageContent(msg, index);
});

const MessageRow = React.memo(({
  msg,
  index,
  groupType,
  currentStyles,
  engineeringTeamRoleSet,
  mentorRoleMapSafe,
  onAgentClick,
  renderMessageContent,
}) => {
  if (!msg) return null;

  const messageId = msg.id || `msg-${index}`;
  const messageStyle =
    groupType === 'newGroup' || groupType === 'first'
      ? MESSAGE_STYLE_WITH_TOP
      : MESSAGE_STYLE_BASE;

  const isHumanMessage = msg.type === "human" || msg.role === "Human";

  let clickableAgentName = null;
  if (!isHumanMessage) {
    if (
      msg.agentName &&
      msg.agentName !== "Tips" &&
      msg.agentName !== "智能体推荐" &&
      msg.agentName !== "Human" &&
      engineeringTeamRoleSet.has(msg.agentName)
    ) {
      clickableAgentName = msg.agentName;
    } else if (
      msg.role &&
      msg.role !== "System" &&
      msg.role !== "Expert" &&
      msg.role !== "Human" &&
      msg.role !== "tips" &&
      msg.role !== "Tips" &&
      engineeringTeamRoleSet.has(msg.role)
    ) {
      clickableAgentName = msg.role;
    }

    if (msg.type === "System" || msg.type === "expert_recommendations" || (msg.type === "MsgtoHuman" && !msg.agentName)) {
      clickableAgentName = null;
    }

    if (clickableAgentName && mentorRoleMapSafe.has(clickableAgentName)) {
      clickableAgentName = mentorRoleMapSafe.get(clickableAgentName);
    }
  }

  return (
    <div
      key={messageId}
      className={`${currentStyles.message} ${
        isHumanMessage ? currentStyles.messageRight : currentStyles.messageLeft
      }`}
      style={messageStyle}
      data-role={msg.role}
      data-message-id={messageId}
    >
      {msg.type !== "Micro_scene" && (
        <>
          <MessageHeader
            msg={msg}
            currentStyles={currentStyles}
            clickableAgentName={clickableAgentName}
            onAgentClick={onAgentClick}
          />
          <MessageBody
            msg={msg}
            index={index}
            currentStyles={currentStyles}
            renderMessageContent={renderMessageContent}
          />
        </>
      )}
    </div>
  );
});

// 稳定的 Footer 组件：通过 Virtuoso 的 context prop 接收动态数据，组件引用永远不变
const VirtuosoFooter = ({ context }) => {
  if (!context || !context.streamingMsg) return <div style={FOOTER_STYLE} />;
  return (
    <>
      <MessageRow
        msg={context.streamingMsg}
        index={context.streamingIndex}
        groupType={context.streamingGroupType}
        currentStyles={context.currentStyles}
        engineeringTeamRoleSet={context.engineeringTeamRoleSet}
        mentorRoleMapSafe={context.mentorRoleMapSafe}
        onAgentClick={context.onAgentClick}
        renderMessageContent={context.renderMessageContent}
      />
      <div style={FOOTER_STYLE} />
    </>
  );
};

const VIRTUOSO_COMPONENTS = { Footer: VirtuosoFooter };

// 简化的消息列表组件 - 使用 Virtuoso 实现虚拟列表
const VirtualMessageList = ({ 
  messages, 
  welcomeMessage, 
  renderMessageContent, 
  chatPanelRef,
  // styles prop might be passed from parent, but we also import it. 
  // We'll use the imported one as default or merge if needed, 
  // but usually App.js passes the same module.
  styles: propStyles,
  isAutoScrollEnabled = true,
  isViewingHistory = false, // 新增参数，用于判断用户是否在查看历史
  additionalProps = EMPTY_OBJECT, 
  onAgentClick,
  apiUrl
}) => {
  const virtuosoRef = useRef(null);
  
  // 🆕 架构升级：从API动态获取角色配置（支持 GoMAown/CCMA 双后端）
  const { teamRoles: engineeringTeamRoles, mentorRoleMap: mentorToRoleMap } = useRolesConfig(apiUrl);
  const engineeringTeamRoleSet = useMemo(
    () => new Set(engineeringTeamRoles || []),
    [engineeringTeamRoles]
  );
  const mentorRoleMapSafe = useMemo(
    () => new Map(Object.entries(mentorToRoleMap || {})),
    [mentorToRoleMap]
  );
  
  // 使用传入的样式或导入的样式
  const currentStyles = propStyles || styles;

  // 将 messages 拆分为：稳定列表（给 Virtuoso）+ 流式消息（独立渲染）
  const lastMsg = messages[messages.length - 1];
  const isLastStreaming = lastMsg && lastMsg.isStreaming;

  // 流式结束后延迟归入主列表，避免 Footer→Virtuoso 切换导致滚动跳动
  const [deferredStreaming, setDeferredStreaming] = React.useState(false);
  const deferTimerRef = useRef(null);

  useEffect(() => {
    if (isLastStreaming) {
      setDeferredStreaming(true);
      if (deferTimerRef.current) {
        clearTimeout(deferTimerRef.current);
        deferTimerRef.current = null;
      }
    } else if (deferredStreaming) {
      // 流式刚结束，延迟归入主列表
      deferTimerRef.current = setTimeout(() => {
        setDeferredStreaming(false);
        deferTimerRef.current = null;
      }, 400);
    }
    return () => {
      if (deferTimerRef.current) clearTimeout(deferTimerRef.current);
    };
  }, [isLastStreaming]); // eslint-disable-line react-hooks/exhaustive-deps

  const keepInFooter = isLastStreaming || deferredStreaming;

  // 稳定消息列表：流式期间排除最后一条，避免 Virtuoso 高度修正抖动
  const stableMessages = useMemo(() => {
    if (keepInFooter && messages.length > 0) {
      return messages.slice(0, -1);
    }
    return messages;
  }, [messages, keepInFooter]);

  // 流式消息：单独提取，不进入 Virtuoso
  const streamingMsg = keepInFooter ? lastMsg : null;

  const allMessages = useMemo(() => {
    const result = [...stableMessages];
    if (welcomeMessage) {
      const welcomeMsg = {
        id: 'welcome-message',
        role: 'System',
        type: 'System',
        content: welcomeMessage.context,
        agentName: welcomeMessage.agentName,
        isWelcomeMessage: true,
        timestamp: new Date(Date.now() - 1000).toISOString(),
      };
      if (!result.some(m => m.id === 'welcome-message')) {
        result.unshift(welcomeMsg);
      }
    }
    return result;
  }, [stableMessages, welcomeMessage]);

  const scrollerElRef = useRef(null);

  // 用户是否"接近底部"：用宽松阈值（200px），而非 Virtuoso 严格的 isAtBottom
  const isNearBottom = useCallback(() => {
    const el = scrollerElRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 400;
  }, []);

  // 用户主动向上滚动超过阈值时，标记为"脱离底部"，停止自动跟随
  const userScrolledAway = useRef(false);
  const handleScrollerScroll = useCallback(() => {
    userScrolledAway.current = !isNearBottom();
  }, [isNearBottom]);

  // 监听 scroller 的 scroll 事件
  const attachScrollListener = useCallback((el) => {
    scrollerElRef.current = el;
    if (el) {
      el.addEventListener('scroll', handleScrollerScroll, { passive: true });
    }
  }, [handleScrollerScroll]);

  useEffect(() => {
    return () => {
      const el = scrollerElRef.current;
      if (el) el.removeEventListener('scroll', handleScrollerScroll);
    };
  }, [handleScrollerScroll]);

  // 安全滚到底：用 DOM scrollTop，一次 rAF，不抖动
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollerElRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  // 新消息追加时（非流式），滚到底部
  const prevMsgCount = useRef(allMessages.length);
  useEffect(() => {
    if (allMessages.length > prevMsgCount.current && !userScrolledAway.current && !isViewingHistory) {
      scrollToBottom();
    }
    prevMsgCount.current = allMessages.length;
  }, [allMessages.length, isViewingHistory, scrollToBottom]);

  // 流式消息内容变化时，滚到底部（150ms 节流）
  const streamingContentLen = streamingMsg ? (streamingMsg.context || streamingMsg.content || "").length : 0;
  const scrollThrottleRef = useRef(0);
  const scrollRafRef = useRef(null);
  useEffect(() => {
    if (!keepInFooter || userScrolledAway.current || isViewingHistory) return;
    const now = Date.now();
    if (now - scrollThrottleRef.current < 150) return;
    scrollThrottleRef.current = now;
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      const el = scrollerElRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, [streamingContentLen, keepInFooter, isViewingHistory]);

  // 消息从 Footer 归入 Virtuoso 主列表时（keepInFooter: true→false），强制多次滚底
  const wasInFooter = useRef(false);
  useEffect(() => {
    if (wasInFooter.current && !keepInFooter && !userScrolledAway.current && !isViewingHistory) {
      const el = scrollerElRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight;
          setTimeout(() => { if (!userScrolledAway.current) el.scrollTop = el.scrollHeight; }, 50);
          setTimeout(() => { if (!userScrolledAway.current) el.scrollTop = el.scrollHeight; }, 150);
          setTimeout(() => { if (!userScrolledAway.current) el.scrollTop = el.scrollHeight; }, 300);
        });
      });
    }
    wasInFooter.current = keepInFooter;
  }, [keepInFooter, isViewingHistory]);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  const messageGroups = useMemo(() => {
    return allMessages.map((_, index) => getMessageGroupType(allMessages, index));
  }, [allMessages]);

  const renderMessage = useCallback((index, msg) => {
    return (
      <MessageRow
        msg={msg}
        index={index}
        groupType={messageGroups[index]}
        currentStyles={currentStyles}
        engineeringTeamRoleSet={engineeringTeamRoleSet}
        mentorRoleMapSafe={mentorRoleMapSafe}
        onAgentClick={onAgentClick}
        renderMessageContent={renderMessageContent}
      />
    );
  }, [
    currentStyles,
    engineeringTeamRoleSet,
    mentorRoleMapSafe,
    messageGroups,
    onAgentClick,
    renderMessageContent
  ]);

  // 流式消息的 groupType（相对于 stableMessages 的最后一条）
  const streamingGroupType = useMemo(() => {
    if (!streamingMsg) return 'first';
    const prev = allMessages[allMessages.length - 1];
    if (!prev) return 'first';
    if (streamingMsg.role !== prev.role || streamingMsg.type !== prev.type) return 'newGroup';
    return 'sameGroup';
  }, [streamingMsg, allMessages]);

  // Virtuoso context：将流式消息数据通过 context 传给稳定的 Footer 组件
  const virtuosoContext = useMemo(() => ({
    streamingMsg,
    streamingGroupType,
    streamingIndex: allMessages.length,
    currentStyles,
    engineeringTeamRoleSet,
    mentorRoleMapSafe,
    onAgentClick,
    renderMessageContent,
  }), [
    streamingMsg, streamingGroupType, allMessages.length,
    currentStyles, engineeringTeamRoleSet, mentorRoleMapSafe,
    onAgentClick, renderMessageContent,
  ]);

  return (
    <div 
      className={currentStyles.messagesListContainer} 
      style={FULL_SIZE_STYLE}
    >
      <Virtuoso
        ref={virtuosoRef}
        scrollerRef={attachScrollListener}
        style={FULL_SIZE_STYLE}
        data={allMessages}
        context={virtuosoContext}
        computeItemKey={(index, item) => item.id || `msg-${index}`}
        itemContent={renderMessage}
        followOutput={false}
        alignToBottom={false}
        overscan={{ main: 1200, reverse: 1200 }}
        defaultItemHeight={230}
        increaseViewportBy={{ top: 800, bottom: 800 }}
        components={VIRTUOSO_COMPONENTS}
      />
    </div>
  );
};

export default React.memo(VirtualMessageList);
